(function () {
  const FILTER_CONFIGS = [
    { key: 'colors', label: 'Color', param: 'cf_color' },
    { key: 'sizes', label: 'Size', param: 'cf_size' },
    { key: 'measures', label: 'Measure', param: 'cf_measure' },
    { key: 'options', label: 'Option', param: 'cf_option' },
  ];

  const COLLECTION_FILTER_CACHE = new Map();

  const COLOR_LABELS = {
    black: 'Black',
    blue: 'Blue',
    brown: 'Brown',
    burgundy: 'Burgundy',
    clear: 'Clear',
    gold: 'Gold',
    gray: 'Gray',
    grey: 'Grey',
    green: 'Green',
    lilac: 'Lilac',
    navy: 'Navy',
    nude: 'Nude',
    pink: 'Pink',
    purple: 'Purple',
    red: 'Red',
    rose: 'Rose',
    silver: 'Silver',
    tan: 'Tan',
    teal: 'Teal',
    white: 'White',
  };

  const SIZE_LABELS = {
    xs: 'XS',
    s: 'S',
    m: 'M',
    l: 'L',
    xl: 'XL',
    xxl: 'XXL',
    xxxl: 'XXXL',
    '2x': '2X',
    '3x': '3X',
    '4x': '4X',
    '5x': '5X',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    os: 'OS',
  };

  const MEASURE_PATTERN = /^(\d+(?:[.,]\d+)?)\s*(cm|mm|ml|oz|inch|inches)$/i;

  function createEmptyMeta() {
    return { colors: [], sizes: [], measures: [], options: [] };
  }

  function createLabelMaps() {
    return {
      colors: new Map(),
      sizes: new Map(),
      measures: new Map(),
      options: new Map(),
    };
  }

  function slugify(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function pushUnique(list, value) {
    if (value && !list.includes(value)) list.push(value);
  }

  function normalizeMeasure(value) {
    const collapsed = (value || '').replace(/\s+/g, ' ').trim();
    const match = collapsed.match(MEASURE_PATTERN);

    if (!match) return collapsed;

    return `${match[1]} ${match[2].toLowerCase()}`;
  }

  function getPrettyColor(value) {
    return COLOR_LABELS[slugify(value)] || '';
  }

  function getPrettySize(value) {
    return SIZE_LABELS[slugify(value)] || '';
  }

  function classifyOptionLabel(label) {
    const cleanedLabel = (label || '').replace(/\s+/g, ' ').trim();
    const meta = createEmptyMeta();

    if (!cleanedLabel) return meta;

    if (MEASURE_PATTERN.test(cleanedLabel)) {
      pushUnique(meta.measures, normalizeMeasure(cleanedLabel));
      return meta;
    }

    const segments = cleanedLabel.includes(' / ') ? cleanedLabel.split(' / ') : [cleanedLabel];

    segments.forEach((segment) => {
      const cleanedSegment = segment.trim();
      if (!cleanedSegment) return;

      if (MEASURE_PATTERN.test(cleanedSegment)) {
        pushUnique(meta.measures, normalizeMeasure(cleanedSegment));
        return;
      }

      const slashParts = cleanedSegment.includes('/')
        ? cleanedSegment.split('/').map((part) => part.trim()).filter(Boolean)
        : [cleanedSegment];

      if (slashParts.length > 1) {
        const colorParts = slashParts.map(getPrettyColor);
        if (colorParts.every(Boolean)) {
          colorParts.forEach((value) => pushUnique(meta.colors, value));
          return;
        }

        const sizeParts = slashParts.map(getPrettySize);
        if (sizeParts.every(Boolean)) {
          sizeParts.forEach((value) => pushUnique(meta.sizes, value));
          return;
        }
      }

      const prettyColor = getPrettyColor(cleanedSegment);
      if (prettyColor) {
        pushUnique(meta.colors, prettyColor);
        return;
      }

      const prettySize = getPrettySize(cleanedSegment);
      if (prettySize) {
        pushUnique(meta.sizes, prettySize);
        return;
      }

      pushUnique(meta.options, cleanedSegment);
    });

    if (!meta.colors.length && !meta.sizes.length && !meta.measures.length && !meta.options.length) {
      pushUnique(meta.options, cleanedLabel);
    }

    return meta;
  }

  function readStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const state = {};

    FILTER_CONFIGS.forEach(({ key, param }) => {
      state[key] = new Set(
        (params.get(param) || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      );
    });

    return state;
  }

  function getBaseQueryParams() {
    const params = new URLSearchParams(window.location.search);

    FILTER_CONFIGS.forEach(({ param }) => {
      params.delete(param);
    });

    params.delete('page');
    params.delete('section_id');

    return params;
  }

  function getProductCountLabel(count) {
    return `${count} product${count === 1 ? '' : 's'}`;
  }

  function getPrimaryCardUrl(cardItem) {
    return cardItem.querySelector('.card__heading a')?.getAttribute('href') || '#';
  }

  function getRecordPriority(record) {
    return (record.hasAvailable ? 100000 : 0) + record.productCount * 100 + record.optionCount;
  }

  function buildRecordFromCard(card, sortIndex) {
    const optionLabels = (card.dataset.familyOptions || '')
      .split('||')
      .map((value) => value.trim())
      .filter(Boolean);

    const meta = createEmptyMeta();
    const labels = createLabelMaps();

    optionLabels.forEach((label) => {
      const classified = classifyOptionLabel(label);

      FILTER_CONFIGS.forEach(({ key }) => {
        classified[key].forEach((displayLabel) => {
          const slug = slugify(displayLabel);
          if (!slug) return;

          pushUnique(meta[key], slug);

          if (!labels[key].has(slug)) {
            labels[key].set(slug, displayLabel);
          }
        });
      });
    });

    return {
      familyKey: card.dataset.familyKey || card.dataset.familyTitle || `family-${sortIndex}`,
      title: card.dataset.familyTitle || '',
      html: card.outerHTML,
      optionLabels,
      meta,
      labels,
      optionCount: optionLabels.length,
      productCount: parseInt(
        card.dataset.familyProductCount || card.dataset.familyOptionCount || optionLabels.length || '1',
        10
      ) || 1,
      hasAvailable:
        card.dataset.familyHasAvailable === 'true' || !card.querySelector('.card-collection-stock[disabled]'),
      sortIndex,
      sourcePriority: 0,
    };
  }

  function buildRecordsFromCards(cards, pageNumber) {
    return Array.from(cards)
      .map((card, index) => buildRecordFromCard(card, (pageNumber - 1) * 1000 + index))
      .filter(Boolean);
  }

  function buildRecordsFromHtml(html, pageNumber) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    return buildRecordsFromCards(parsedHtml.querySelectorAll('#product-grid [data-grouped-product-card]'), pageNumber);
  }

  function cloneRecord(record) {
    return {
      familyKey: record.familyKey,
      title: record.title,
      html: record.html,
      optionLabels: [...record.optionLabels],
      meta: {
        colors: [...record.meta.colors],
        sizes: [...record.meta.sizes],
        measures: [...record.meta.measures],
        options: [...record.meta.options],
      },
      labels: {
        colors: new Map(record.labels.colors),
        sizes: new Map(record.labels.sizes),
        measures: new Map(record.labels.measures),
        options: new Map(record.labels.options),
      },
      optionCount: record.optionCount,
      productCount: record.productCount,
      hasAvailable: record.hasAvailable,
      sortIndex: record.sortIndex,
      sourcePriority: getRecordPriority(record),
    };
  }

  function updateGroupedBadge(cardItem, record) {
    const media = cardItem.querySelector('.card__media');
    let badge = cardItem.querySelector('.product-family-badge');

    if (record.productCount > 1) {
      if (!badge && media) {
        badge = document.createElement('div');
        badge.className = 'product-family-badge';

        const count = document.createElement('span');
        count.className = 'product-family-badge__count';

        const label = document.createElement('span');
        label.className = 'product-family-badge__label';
        label.textContent = 'options';

        badge.appendChild(count);
        badge.appendChild(label);
        media.appendChild(badge);
      }

      if (badge) {
        badge.setAttribute('aria-label', `Available in ${record.productCount} options`);

        const countElement = badge.querySelector('.product-family-badge__count');
        if (countElement) {
          countElement.textContent = `${record.productCount}`;
        }

        const labelElement = badge.querySelector('.product-family-badge__label');
        if (labelElement) {
          labelElement.textContent = 'options';
        }
      }
    } else if (badge) {
      badge.remove();
    }
  }

  function updateGroupedTitle(cardItem, record) {
    if (record.productCount <= 1 || !record.title) return;

    cardItem.querySelectorAll('.card__heading a').forEach((link) => {
      link.textContent = record.title;
    });
  }

  function updateGroupedActions(cardItem, record, labels) {
    const footer = cardItem.querySelector('.card-collection-footer');
    if (!footer || record.productCount <= 1) return;

    const wrapper = cardItem.querySelector('.product-card-wrapper');
    if (wrapper) {
      wrapper.classList.add('product-card-wrapper--grouped');
    }

    footer
      .querySelectorAll('product-form, modal-opener, quick-add-modal, .card-collection-cart, .card-collection-stock')
      .forEach((node) => node.remove());

    if (record.hasAvailable) {
      const link = document.createElement('a');
      link.href = getPrimaryCardUrl(cardItem);
      link.className = 'card-collection-cart card-collection-cart--link';
      link.setAttribute('aria-label', `${labels.chooseOptions} ${record.title}`.trim());
      link.textContent = labels.chooseOptions;
      footer.appendChild(link);
      return;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'card-collection-stock';
    button.disabled = true;
    button.setAttribute('aria-label', labels.soldOut);
    button.textContent = labels.soldOut;
    footer.appendChild(button);
  }

  function finalizeRecordHtml(record, labels) {
    const template = document.createElement('template');
    template.innerHTML = record.html.trim();

    const cardItem = template.content.firstElementChild;
    if (!cardItem) return record.html;

    cardItem.dataset.familyKey = record.familyKey;
    cardItem.dataset.familyTitle = record.title;
    cardItem.dataset.familyOptions = record.optionLabels.join('||');
    cardItem.dataset.familyOptionCount = `${record.optionCount}`;
    cardItem.dataset.familyProductCount = `${record.productCount}`;
    cardItem.dataset.familyHasAvailable = record.hasAvailable ? 'true' : 'false';

    updateGroupedBadge(cardItem, record);
    updateGroupedTitle(cardItem, record);
    updateGroupedActions(cardItem, record, labels);

    return cardItem.outerHTML;
  }

  function mergeCollectionRecords(records, labels) {
    const mergedRecords = new Map();

    records.forEach((record) => {
      const familyKey = record.familyKey || `${record.sortIndex}`;

      if (!mergedRecords.has(familyKey)) {
        mergedRecords.set(familyKey, cloneRecord(record));
        return;
      }

      const existing = mergedRecords.get(familyKey);

      existing.sortIndex = Math.min(existing.sortIndex, record.sortIndex);
      existing.productCount += record.productCount;
      existing.hasAvailable = existing.hasAvailable || record.hasAvailable;

      if (!existing.title && record.title) {
        existing.title = record.title;
      }

      record.optionLabels.forEach((label) => {
        pushUnique(existing.optionLabels, label);
      });

      FILTER_CONFIGS.forEach(({ key }) => {
        record.meta[key].forEach((slug) => {
          pushUnique(existing.meta[key], slug);
        });

        record.labels[key].forEach((displayLabel, slug) => {
          if (!existing.labels[key].has(slug)) {
            existing.labels[key].set(slug, displayLabel);
          }
        });
      });

      const nextPriority = getRecordPriority(record);
      if (nextPriority > existing.sourcePriority) {
        existing.html = record.html;
        existing.sourcePriority = nextPriority;
      }
    });

    return Array.from(mergedRecords.values())
      .sort((left, right) => left.sortIndex - right.sortIndex)
      .map((record) => {
        record.optionCount = record.optionLabels.length;
        record.html = finalizeRecordHtml(record, labels);
        return record;
      });
  }

  class CollectionOptionFilters {
    constructor(container) {
      this.container = container;
      this.isDestroyed = false;
      this.handleClick = this.handleClick.bind(this);

      this.container.addEventListener('click', this.handleClick);
      this.refresh();
    }

    destroy() {
      this.isDestroyed = true;
      this.container.removeEventListener('click', this.handleClick);
    }

    refresh() {
      this.gridContainer = document.getElementById('ProductGridContainer');
      this.grid = document.getElementById('product-grid');
      this.productCount = document.getElementById('ProductCount');
      this.productCountDesktop = document.getElementById('ProductCountDesktop');

      if (!this.gridContainer || !this.grid) {
        this.container.hidden = true;
        return;
      }

      this.collectionRoot = this.grid.closest('.collection');
      this.gridItemsContainer =
        this.grid.matches('ul, ol') ? this.grid : this.grid.querySelector('.product-grid') || this.grid;
      this.paginationWrapper = this.collectionRoot?.querySelector('.pagination-wrapper') || null;
      this.chooseOptionsLabel = this.gridContainer.dataset.chooseOptionsLabel || 'Choose options';
      this.soldOutLabel = this.gridContainer.dataset.soldOutLabel || 'Out of stock';
      this.sectionId = this.grid.dataset.id || this.gridItemsContainer.dataset.id || '';
      this.currentPage = parseInt(this.gridContainer.dataset.currentPage || '1', 10) || 1;
      this.totalPages = parseInt(this.gridContainer.dataset.totalPages || '1', 10) || 1;
      this.baseParams = getBaseQueryParams();
      this.state = readStateFromUrl();
      this.pageRecords = buildRecordsFromCards(
        this.gridItemsContainer.querySelectorAll('[data-grouped-product-card]'),
        this.currentPage
      );
      this.records = this.pageRecords;
      this.originalGridHTML = this.gridItemsContainer.innerHTML;
      this.originalCountHTML = this.productCount ? this.productCount.innerHTML : '';
      this.originalCountDesktopHTML = this.productCountDesktop ? this.productCountDesktop.innerHTML : '';
      this.hasCollectionData = this.totalPages <= 1;
      this.isLoadingCollection = false;
      this.gridShowsOriginal = true;
      this.hasRequestedCollectionRecords = false;

      this.rebuildAvailableValues();
      this.normalizeState();
      this.render();
      this.applyFilters();
      this.syncHiddenInputs();
      this.updateLinkedUrls();
      this.updateUrl();

      const hasActiveFiltersOnLoad = FILTER_CONFIGS.some(({ key }) => this.state[key].size > 0);
      if (hasActiveFiltersOnLoad) this.ensureCollectionRecordsLoaded();
    }

    rebuildAvailableValues() {
      this.availableValues = createLabelMaps();

      this.records.forEach((record) => {
        FILTER_CONFIGS.forEach(({ key }) => {
          record.labels[key].forEach((displayLabel, slug) => {
            if (!this.availableValues[key].has(slug)) {
              this.availableValues[key].set(slug, displayLabel);
            }
          });
        });
      });
    }

    normalizeState() {
      FILTER_CONFIGS.forEach(({ key }) => {
        const entries = Array.from(this.availableValues[key].entries());

        if (this.shouldSuppressGroup(key, entries)) {
          this.availableValues[key] = new Map();
          this.state[key].clear();
          return;
        }

        this.state[key] = new Set(
          Array.from(this.state[key]).filter((slug) => this.availableValues[key].has(slug))
        );
      });
    }

    shouldSuppressGroup(filterKey, entries) {
      if (filterKey !== 'options') return false;

      return entries.some(([, displayLabel]) => displayLabel.length > 24 || displayLabel.split(/\s+/).length > 4);
    }

    ensureCollectionRecordsLoaded() {
      if (this.hasRequestedCollectionRecords) return;
      this.hasRequestedCollectionRecords = true;
      this.loadCollectionRecords();
    }

    async loadCollectionRecords() {
      if (!this.sectionId || this.totalPages <= 1) return;

      const cacheKey = `${window.location.pathname}::${this.sectionId}::${this.baseParams.toString()}::${this.totalPages}`;
      this.requestKey = cacheKey;

      if (COLLECTION_FILTER_CACHE.has(cacheKey)) {
        this.applyCollectionRecords(cacheKey, COLLECTION_FILTER_CACHE.get(cacheKey));
        return;
      }

      this.isLoadingCollection = true;
      this.applyFilters();

      const pageRequests = [];

      for (let page = 1; page <= this.totalPages; page += 1) {
        if (page === this.currentPage) continue;

        const params = new URLSearchParams(this.baseParams);
        params.set('page', `${page}`);
        params.set('section_id', this.sectionId);

        pageRequests.push(
          fetch(`${window.location.pathname}?${params.toString()}`)
            .then((response) => response.text())
            .then((html) => buildRecordsFromHtml(html, page))
            .catch(() => [])
        );
      }

      const fetchedRecords = await Promise.all(pageRequests);

      if (this.isDestroyed || this.requestKey !== cacheKey) return;

      const mergedRecords = mergeCollectionRecords(
        [...this.pageRecords, ...fetchedRecords.flat()],
        {
          chooseOptions: this.chooseOptionsLabel,
          soldOut: this.soldOutLabel,
        }
      );

      COLLECTION_FILTER_CACHE.set(cacheKey, mergedRecords);
      this.applyCollectionRecords(cacheKey, mergedRecords);
    }

    applyCollectionRecords(cacheKey, records) {
      if (this.isDestroyed || this.requestKey !== cacheKey) return;

      this.records = records;
      this.hasCollectionData = true;
      this.isLoadingCollection = false;
      this.rebuildAvailableValues();
      this.normalizeState();
      this.rerender();
    }

    cloneState() {
      const state = {};

      FILTER_CONFIGS.forEach(({ key }) => {
        state[key] = new Set(this.state[key]);
      });

      return state;
    }

    cardMatchesState(record, state) {
      const meta = record.meta || createEmptyMeta();

      return FILTER_CONFIGS.every(({ key }) => {
        const selectedValues = state[key];
        if (!selectedValues || !selectedValues.size) return true;

        return meta[key].some((value) => selectedValues.has(value));
      });
    }

    getOptionCount(filterKey, filterValue) {
      const state = this.cloneState();
      state[filterKey].add(filterValue);

      return this.records.reduce((count, record) => {
        return count + (this.cardMatchesState(record, state) ? 1 : 0);
      }, 0);
    }

    render() {
      const openGroups = new Set(
        Array.from(this.container.querySelectorAll('details[open]')).map((el) => el.dataset.optionFilterGroup)
      );

      this.container.innerHTML = '';

      const fragment = document.createDocumentFragment();
      let hasVisibleGroups = false;

      FILTER_CONFIGS.forEach((config) => {
        const selectedValues = this.state[config.key];
        const entries = Array.from(this.availableValues[config.key].entries());
        const shouldRenderGroup = entries.length > 1 || selectedValues.size > 0;

        if (!shouldRenderGroup) return;

        hasVisibleGroups = true;

        const details = document.createElement('details');
        details.className = 'disclosure-has-popup facets__disclosure collection-option-filter';
        details.dataset.optionFilterGroup = config.key;
        if (openGroups.has(config.key)) details.open = true;
        details.addEventListener('toggle', () => {
          if (details.open) this.ensureCollectionRecordsLoaded();
        });

        const summary = document.createElement('summary');
        summary.className = 'facets__summary caption-large focus-offset';
        summary.setAttribute(
          'aria-label',
          selectedValues.size > 0 ? `${config.label} (${selectedValues.size} selected)` : config.label
        );
        summary.innerHTML = `
          <div>
            <span class="facets__summary-label">
              ${config.label}
              ${selectedValues.size > 0 ? `<span class="collection-option-filter__count">(${selectedValues.size})</span>` : ''}
            </span>
            <span class="collection-option-filter__caret" aria-hidden="true"></span>
          </div>
        `;

        const display = document.createElement('div');
        display.className = 'facets__display collection-option-filter__display';

        const header = document.createElement('div');
        header.className = 'facets__header collection-option-filter__header';

        const selectedText = document.createElement('span');
        selectedText.className = 'facets__selected';
        selectedText.textContent =
          selectedValues.size > 0 ? `${selectedValues.size} selected` : 'Select one or more options';

        header.appendChild(selectedText);

        if (selectedValues.size > 0) {
          const clearGroupButton = document.createElement('button');
          clearGroupButton.type = 'button';
          clearGroupButton.className = 'facets__reset link underlined-link collection-option-filter__clear';
          clearGroupButton.dataset.optionFilterClearGroup = config.key;
          clearGroupButton.textContent = 'Clear';
          header.appendChild(clearGroupButton);
        }

        const values = document.createElement('div');
        values.className = 'collection-option-filter__values';

        entries.forEach(([slug, displayLabel]) => {
          const isSelected = selectedValues.has(slug);
          const optionCount = this.getOptionCount(config.key, slug);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'collection-option-filter__value';
          button.dataset.optionFilterValue = slug;
          button.dataset.filterKey = config.key;
          button.innerHTML = `
            <span class="collection-option-filter__value-label">${displayLabel}</span>
            <span class="collection-option-filter__value-count">(${optionCount})</span>
          `;

          if (isSelected) {
            button.classList.add('is-active');
            button.setAttribute('aria-pressed', 'true');
          } else {
            button.setAttribute('aria-pressed', 'false');
          }

          if (optionCount === 0 && !isSelected) {
            button.classList.add('is-disabled');
            button.disabled = true;
          }

          button.setAttribute('aria-label', `${displayLabel} (${optionCount} products)`);

          values.appendChild(button);
        });

        display.appendChild(header);
        display.appendChild(values);
        details.appendChild(summary);
        details.appendChild(display);
        fragment.appendChild(details);
      });

      const clearButton = document.createElement('button');
      clearButton.type = 'button';
      clearButton.className = 'collection-option-filters__clear';
      clearButton.dataset.optionFilterClear = 'true';
      clearButton.textContent = 'Clear';
      clearButton.hidden = true;
      fragment.appendChild(clearButton);

      const status = document.createElement('span');
      status.className = 'collection-option-filters__status';
      status.dataset.optionFilterStatus = 'true';
      status.hidden = true;
      fragment.appendChild(status);

      this.container.appendChild(fragment);
      this.clearButton = this.container.querySelector('[data-option-filter-clear]');
      this.statusElement = this.container.querySelector('[data-option-filter-status]');
      this.container.hidden = !hasVisibleGroups;
    }

    restoreOriginalGrid() {
      if (this.gridItemsContainer) {
        this.gridItemsContainer.innerHTML = this.originalGridHTML;
      }

      if (this.paginationWrapper) {
        this.paginationWrapper.hidden = false;
      }
    }

    renderFilteredGrid(records) {
      if (this.gridItemsContainer) {
        this.gridItemsContainer.innerHTML = records.map((record) => record.html).join('');
      }

      if (this.paginationWrapper) {
        this.paginationWrapper.hidden = true;
      }
    }

    updateProductCount(visibleCount, hasActiveFilters) {
      if (!hasActiveFilters) {
        if (this.productCount) {
          this.productCount.innerHTML = this.originalCountHTML;
        }

        if (this.productCountDesktop) {
          this.productCountDesktop.innerHTML = this.originalCountDesktopHTML;
        }

        return;
      }

      const text = getProductCountLabel(visibleCount);

      if (this.productCount) {
        this.productCount.textContent = text;
      }

      if (this.productCountDesktop) {
        this.productCountDesktop.textContent = text;
      }
    }

    applyFilters() {
      const hasActiveFilters = FILTER_CONFIGS.some(({ key }) => this.state[key].size > 0);
      const matchingRecords = this.records.filter((record) => this.cardMatchesState(record, this.state));
      const visibleCount = matchingRecords.length;

      if (hasActiveFilters) {
        this.renderFilteredGrid(matchingRecords);
        this.gridShowsOriginal = false;
      } else if (!this.gridShowsOriginal) {
        this.restoreOriginalGrid();
        this.gridShowsOriginal = true;
      }

      if (this.clearButton) {
        this.clearButton.hidden = !hasActiveFilters;
      }

      this.updateProductCount(visibleCount, hasActiveFilters);

      if (this.statusElement) {
        if (this.isLoadingCollection) {
          this.statusElement.textContent = 'Loading full collection...';
          this.statusElement.hidden = false;
        } else if (hasActiveFilters && visibleCount === 0) {
          this.statusElement.textContent = `No matching products in this ${this.hasCollectionData ? 'collection' : 'page'}`;
          this.statusElement.hidden = false;
        } else if (hasActiveFilters && this.hasCollectionData) {
          this.statusElement.textContent = `Showing ${visibleCount} matching ${visibleCount === 1 ? 'product' : 'products'} in this collection`;
          this.statusElement.hidden = false;
        } else {
          this.statusElement.textContent = '';
          this.statusElement.hidden = true;
        }
      }
    }

    syncHiddenInputs() {
      FILTER_CONFIGS.forEach(({ key, param }) => {
        const value = Array.from(this.state[key]).join(',');

        document.querySelectorAll(`input[name="${param}"]`).forEach((input) => {
          input.value = value;
        });
      });
    }

    updateLinkedUrls() {
      const selectors = [
        '#main-collection-filters a[href]',
        '#main-search-filters a[href]',
        '#ProductGridContainer .pagination a[href]',
      ];

      document.querySelectorAll(selectors.join(',')).forEach((anchor) => {
        const href = anchor.getAttribute('href');

        if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

        const url = new URL(href, window.location.origin);

        FILTER_CONFIGS.forEach(({ key, param }) => {
          const value = Array.from(this.state[key]).join(',');

          if (value) {
            url.searchParams.set(param, value);
          } else {
            url.searchParams.delete(param);
          }
        });

        anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      });
    }

    updateUrl() {
      const params = new URLSearchParams(window.location.search);

      FILTER_CONFIGS.forEach(({ key, param }) => {
        const value = Array.from(this.state[key]).join(',');

        if (value) {
          params.set(param, value);
        } else {
          params.delete(param);
        }
      });

      const searchParams = params.toString();
      const nextUrl = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
      window.history.replaceState({ searchParams }, '', nextUrl);
    }

    rerender() {
      this.render();
      this.applyFilters();
      this.syncHiddenInputs();
      this.updateLinkedUrls();
      this.updateUrl();
    }

    handleClick(event) {
      const clearButton = event.target.closest('[data-option-filter-clear]');
      if (clearButton) {
        FILTER_CONFIGS.forEach(({ key }) => {
          this.state[key].clear();
        });

        this.rerender();
        return;
      }

      const clearGroupButton = event.target.closest('[data-option-filter-clear-group]');
      if (clearGroupButton) {
        const filterKey = clearGroupButton.dataset.optionFilterClearGroup;

        if (this.state[filterKey]) {
          this.state[filterKey].clear();
          this.rerender();
        }

        return;
      }

      const valueButton = event.target.closest('[data-option-filter-value]');
      if (!valueButton) return;

      const filterKey = valueButton.dataset.filterKey;
      const filterValue = valueButton.dataset.optionFilterValue;
      const selectedValues = this.state[filterKey];

      if (!selectedValues) return;

      if (selectedValues.has(filterValue)) {
        selectedValues.delete(filterValue);
      } else {
        selectedValues.add(filterValue);
      }

      this.rerender();
    }
  }

  function initializeCollectionOptionFilters() {
    document.querySelectorAll('[data-collection-option-filters]').forEach((container) => {
      if (container._collectionOptionFilters) {
        container._collectionOptionFilters.destroy();
      }

      container._collectionOptionFilters = new CollectionOptionFilters(container);
    });
  }

  function observeCollectionGridUpdates() {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer || gridContainer._collectionOptionObserver) return;

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(initializeCollectionOptionFilters);
    });

    observer.observe(gridContainer, { childList: true });
    gridContainer._collectionOptionObserver = observer;
  }

  document.addEventListener('DOMContentLoaded', () => {
    initializeCollectionOptionFilters();
    observeCollectionGridUpdates();
  });

  document.addEventListener('shopify:section:load', () => {
    initializeCollectionOptionFilters();
    observeCollectionGridUpdates();
  });

  window.addEventListener('popstate', () => {
    window.requestAnimationFrame(initializeCollectionOptionFilters);
  });
})();
